/**
 * A identidade visual da VincDev, como está no Brand Guidelines v1.0. É o único arquivo a
 * trocar se a marca mudar.
 *
 * Uma base neutra (Ink + Paper) e um único acento, o Volt Blue — que, pela regra 90/10 do
 * guia, aparece em botões, ícones e detalhes, nunca como fundo de área extensa. O Deep Blue
 * é a variante segura para links e texto pequeno sobre fundo claro (contraste AA); o Sky
 * Tint, o único fundo azulado permitido. Space Grotesk assina títulos e o wordmark (nunca
 * abaixo do peso 600); Manrope cuida do corpo de texto e da interface.
 */
type PaletteColorConfig = {
  lighter: string;
  light: string;
  main: string;
  dark: string;
  darker: string;
  contrastText: string;
};

type ThemeConfig = {
  classesPrefix: string;
  fontFamily: { primary: string; secondary: string };
  brand: { ink: string; paper: string; voltBlue: string; deepBlue: string; skyTint: string };
  palette: Record<
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error',
    PaletteColorConfig
  > & {
    grey: Record<
      '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
      string
    >;
    common: { black: string; white: string };
  };
};

const brand = {
  ink: '#14161A',
  paper: '#FFFFFF',
  voltBlue: '#0B84FF',
  deepBlue: '#0A6FD6',
  skyTint: '#E8F2FF',
};

export const themeConfig: ThemeConfig = {
  classesPrefix: 'vd',
  fontFamily: {
    primary: 'Manrope Variable',
    secondary: 'Space Grotesk Variable',
  },
  brand,
  palette: {
    primary: {
      lighter: brand.skyTint,
      light: '#6DB4FF',
      main: brand.voltBlue,
      dark: brand.deepBlue,
      darker: '#084F99',
      contrastText: brand.paper,
    },
    // "Secundário" é a tinta: o guia manda ações secundárias em contorno Ink, nunca em azul.
    secondary: {
      lighter: '#F4F6F8',
      light: '#637381',
      main: brand.ink,
      dark: '#0C0D10',
      darker: '#050506',
      contrastText: brand.paper,
    },
    info: {
      lighter: '#CAFDF5',
      light: '#61F3F3',
      main: '#00B8D9',
      dark: '#006C9C',
      darker: '#003768',
      contrastText: brand.paper,
    },
    success: {
      lighter: '#D3FCD2',
      light: '#77ED8B',
      main: '#22C55E',
      dark: '#118D57',
      darker: '#065E49',
      contrastText: brand.paper,
    },
    warning: {
      lighter: '#FFF5CC',
      light: '#FFD666',
      main: '#FFAB00',
      dark: '#B76E00',
      darker: '#7A4100',
      contrastText: brand.ink,
    },
    error: {
      lighter: '#FFE9D5',
      light: '#FFAC82',
      main: '#FF5630',
      dark: '#B71D18',
      darker: '#7A0916',
      contrastText: brand.paper,
    },
    // Cinzas frios do Minimal, com os dois tons mais escuros trocados pela tinta da marca:
    // são eles que viram as superfícies do modo escuro.
    grey: {
      '50': '#FCFDFD',
      '100': '#F9FAFB',
      '200': '#F4F6F8',
      '300': '#DFE3E8',
      '400': '#C4CDD5',
      '500': '#919EAB',
      '600': '#637381',
      '700': '#454F5B',
      '800': '#1C1F26',
      '900': brand.ink,
    },
    common: { black: '#000000', white: brand.paper },
  },
};
